import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { UserAvatar } from '../../components/userAvatar/UserAvatar';
import {
  authenticateUserFromStorage,
  getLocalStorageAuthHeaders,
  signOutUser,
} from '../sessions/sessionSlice';
import * as REQUEST_REQUIREMENTS from '../../api/requestRequirements';
import { useNavigate } from 'react-router-dom';
import { closeAllDialogueBoxes } from '../sessions/sessionDialogues';
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface UserProfileButtonProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  onSignIn: () => void;
}

const UserProfileButton: React.FC<UserProfileButtonProps> = ({
  isMenuOpen,
  setIsMenuOpen,
  onSignIn,
}) => {
  const currentUser = useSelector((state: RootState) => state.session.currentUser);
  const userValidated = useSelector((state: RootState) => state.session.loggedIn);
  const avatar = useSelector((state: RootState) => state.session.userMoreInfo.avatarUrl);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [PRIVATE_ROUTES, setPrivateRoutes] = useState<REQUEST_REQUIREMENTS.EndPoints>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!userValidated) dispatch(authenticateUserFromStorage());
    if (currentUser) {
      const routes = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: { userNickname: currentUser.nickname } });
      setPrivateRoutes(routes);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!isMenuOpen) setAnchorEl(null);
  }, [isMenuOpen]);

  const handleProfileClick = (e: React.MouseEvent<HTMLElement>) => {
    if (userValidated) {
      setAnchorEl(anchorEl ? null : e.currentTarget);
      setIsMenuOpen(!isMenuOpen);
    } else {
      onSignIn();
    }
  };

  async function handleLogout() {
    const authHeaders = getLocalStorageAuthHeaders();
    await dispatch(signOutUser(authHeaders));
    setAnchorEl(null);
    setIsMenuOpen(false);
    dispatch(closeAllDialogueBoxes());
  }

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ position: 'relative' }}>
      {userValidated ? (
        <IconButton
          onClick={handleProfileClick}
          size="small"
          sx={{
            p: 0.5,
            border: open ? `1px solid rgba(255,255,255,0.4)` : '1px solid transparent',
            borderRadius: '50%',
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
          }}
          aria-label="Perfil"
        >
          <UserAvatar src={currentUser ? avatar : undefined} size={34} />
        </IconButton>
      ) : (
        <Button
          variant="outlined"
          startIcon={<LoginIcon />}
          onClick={onSignIn}
          size="small"
          sx={{
            borderColor: 'rgba(255,255,255,0.5)',
            color: TEXT_PRIMARY,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          Sign In
        </Button>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setIsMenuOpen(false); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        PaperProps={{
          elevation: 0,
          sx: {
            width: 200,
            bgcolor: BG_SECTION,
            border: BORDER_SUBTLE,
            borderRadius: '8px',
            overflow: 'hidden',
            mt: 0.5,
          },
        }}
      >
        <MenuList dense sx={{ p: 0.5 }}>
          <MenuItem
            onClick={() => {
              if (PRIVATE_ROUTES?.USER_CONFIG) {
                setAnchorEl(null);
                setIsMenuOpen(false);
                navigate(PRIVATE_ROUTES.USER_CONFIG);
              }
            }}
            sx={{ borderRadius: '6px', color: TEXT_PRIMARY, '&:hover': { bgcolor: BG_CARD } }}
          >
            <ListItemIcon>
              <SettingsIcon sx={{ color: TEXT_SECONDARY, fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Settings</ListItemText>
          </MenuItem>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }} />

          <MenuItem
            onClick={handleLogout}
            sx={{ borderRadius: '6px', color: 'rgba(255,100,100,0.85)', '&:hover': { bgcolor: 'rgba(192,57,43,0.12)' } }}
          >
            <ListItemIcon>
              <LogoutIcon sx={{ color: 'rgba(255,100,100,0.85)', fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Sign Out</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>
    </Box>
  );
};

export default UserProfileButton;
